import {
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Slider,
  CircularProgress,
  Chip,
  Pagination,
  Stack,
  Box,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  FormGroup
} from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { ArrowDropDown, FilterList, Close, Payment, AccountBalanceWallet, Search as SearchIcon, Visibility } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axiosInstance from "../../utils/axios/axiosInstance";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import loadingGif from '../../assets/images/loading.gif';

// Constants to prevent multiple placeholder calls
const PLACEHOLDER_IMAGE = "https://via.placeholder.com/150";

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Helper function to truncate title
const truncateTitle = (title, maxLength = 25) => {
  if (!title || typeof title !== 'string') return 'Untitled';
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength).trim() + '...';
};

// Helper function to ensure string values
const ensureString = (value, fallback = '') => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return fallback;
  return String(value);
};

// Helper function to extract image URL
const extractImageUrl = (ad) => {
  if (Array.isArray(ad.thumbnail) && ad.thumbnail.length > 0) {
    return ad.thumbnail[0];
  }
  if (ad.thumbnail && typeof ad.thumbnail === 'string') {
    return ad.thumbnail;
  }
  if (ad.image && Array.isArray(ad.image) && ad.image.length > 0) {
    return ad.image[0].thumbnail || ad.image[0].url || ad.image[0];
  }
  if (ad.images && Array.isArray(ad.images) && ad.images.length > 0) {
    return ad.images[0].image_url || ad.images[0].url || ad.images[0];
  }
  return null;
};

// Optimized Image Component
const OptimizedImage = React.memo(({ item, mode, onImageClick }) => {
  const [imageError, setImageError] = useState(false);
  
  const imageUrl = useMemo(() => {
    if (imageError) return PLACEHOLDER_IMAGE;
    return extractImageUrl(item) || PLACEHOLDER_IMAGE;
  }, [item, imageError]);

  const handleImageError = useCallback(() => {
    if (!imageError) {
      setImageError(true);
    }
  }, [imageError]);

  if (!extractImageUrl(item)) {
    return (
      <Box
        sx={{
          height: { xs: "120px", sm: mode === 0 ? "150px" : "100px" },
          bgcolor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9e9e9e',
          width: '100%',
          cursor: onImageClick ? 'pointer' : 'default'
        }}
        onClick={onImageClick}
      >
        <Typography variant="body2">No Image</Typography>
      </Box>
    );
  }

  return (
    <div
      onClick={onImageClick}
      className="w-100"
      style={{
        height: mode === 0 ? "100%" : "100%",
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f5f5f5',
        cursor: onImageClick ? 'pointer' : 'default',
        display: 'block'
      }}
      title={ensureString(item.name, 'Product image')}
    >
      <img
        src={imageUrl}
        alt=""
        onError={handleImageError}
        style={{ display: 'none' }}
      />
    </div>
  );
});

// Heart Icons
const HeartIconFilled = React.memo(() => (
  <svg 
    height="22px" 
    width="22px" 
    fill="#e91e63" 
    viewBox="0 0 512 512" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M340.8,83C307,83,276,98.8,256,124.8c-20-26-51-41.8-84.8-41.8C112.1,83,64,131.3,64,190.7c0,27.9,10.6,54.4,29.9,74.6  L245.1,418l10.9,11l10.9-11l148.3-149.8c21-20.3,32.8-47.9,32.8-77.5C448,131.3,399.9,83,340.8,83L340.8,83z" />
  </svg>
));

const HeartIconOutline = React.memo(() => (
  <svg 
    height="22px" 
    width="22px" 
    fill="none" 
    stroke="#666" 
    strokeWidth="24"
    viewBox="0 0 512 512" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M340.8,83C307,83,276,98.8,256,124.8c-20-26-51-41.8-84.8-41.8C112.1,83,64,131.3,64,190.7c0,27.9,10.6,54.4,29.9,74.6  L245.1,418l10.9,11l10.9-11l148.3-149.8c21-20.3,32.8-47.9,32.8-77.5C448,131.3,399.9,83,340.8,83L340.8,83z" />
  </svg>
));

// Payment Method Icons
const PaymentMethodIcon = React.memo(({ method, paypalId, venmoId }) => {
  const iconStyle = { width: '20px', height: '20px', marginRight: '4px' };
  const safeMethod = ensureString(method, 'cash').toLowerCase();
  const safePaypalId = ensureString(paypalId);
  const safeVenmoId = ensureString(venmoId);
  
  switch (safeMethod) {
    case 'venmo':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <AccountBalanceWallet sx={iconStyle} />
          <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            {`Venmo${safeVenmoId ? `: @${safeVenmoId}` : ''}`}
          </Typography>
        </Box>
      );
    case 'paypal':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Payment sx={iconStyle} />
          <Typography variant="caption" sx={{ wordBreak: 'break-all', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            {`PayPal${safePaypalId ? `: ${safePaypalId}` : ''}`}
          </Typography>
        </Box>
      );
    case 'cash':
    default:
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Payment sx={iconStyle} />
          <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
            Cash
          </Typography>
        </Box>
      );
  }
});

// Wishlist Button Component
const WishlistButton = React.memo(({ item, onToggle }) => {
  const [isWishlisted, setIsWishlisted] = useState(item.is_wishlisted || false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsWishlisted(item.is_wishlisted || false);
  }, [item.is_wishlisted]);

  const handleToggle = useCallback(async (e) => {
    e.stopPropagation();
    
    if (isLoading) return;
    
    setIsLoading(true);
    const newStatus = !isWishlisted;
    const originalStatus = isWishlisted;
    
    try {
      setIsWishlisted(newStatus);
      
      if (onToggle) {
        await onToggle(item.id, newStatus);
      }
      
      console.log(`Wishlist ${newStatus ? 'added' : 'removed'} successfully for item ${item.id}`);
      
    } catch (error) {
      setIsWishlisted(originalStatus);
      console.error('Wishlist toggle failed:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to manage your wishlist');
      } else if (error.response?.status === 404) {
        toast.error('Item not found');
      } else {
        toast.error('Failed to update wishlist. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [item.id, isWishlisted, isLoading, onToggle]);

  return (
    <Button
      className="wishlist-icon"
      variant="outlined"
      size="small"
      onClick={handleToggle}
      disabled={isLoading}
      sx={{
        minWidth: 'unset',
        padding: { xs: '6px', sm: '4px' },
        border: 'none',
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        '&:hover': {
          bgcolor: 'rgba(255, 255, 255, 1)',
          border: 'none',
          transform: 'scale(1.1)'
        },
        '&:disabled': {
          opacity: 0.6
        },
        transition: 'all 0.2s ease'
      }}
    >
      {isLoading ? (
        <CircularProgress size={22} />
      ) : isWishlisted ? (
        <HeartIconFilled />
      ) : (
        <HeartIconOutline />
      )}
    </Button>
  );
});

// Product Card Component
const ProductCard = React.memo(({ item, mode, onItemClick, onWishlistToggle, getStatusColor, renderSubcategories }) => {
  const handleCardClick = useCallback(() => {
    if (onItemClick) {
      onItemClick(item.id);
    }
  }, [item.id, onItemClick]);

  if (mode === 0) {
    return (
      <div className="h-100">
        <div 
          className="product-card" 
          onClick={handleCardClick} 
          style={{ 
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        >
          <div className="product-media" style={{ position: 'relative' }}>
            <div className="product-img">
              <OptimizedImage item={item} mode={mode} onImageClick={handleCardClick} />
            </div>
            
            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
              <WishlistButton item={item} onToggle={onWishlistToggle} />
            </div>
            
            <span className={`badge ${getStatusColor(item.status)}`} style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {ensureString(item.status, 'Unknown')}
            </span>
          </div>
          
          <Box sx={{ padding: { xs: '12px', sm: '16px' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  fontSize: { xs: '11px', sm: '12px' }, 
                  display: 'block'
                }}
              >
                Posted: {ensureString(item.formattedDate, 'N/A')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Visibility sx={{ fontSize: '14px', color: '#666' }} />
                <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '11px', sm: '12px' } }}>
                  {ensureString(item.views || item.view_count || '0', '0')}
                </Typography>
              </Box>
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: { xs: '16px', sm: '18px' }, 
                fontWeight: 'bold', 
                color: '#2e7d32', 
                mb: 1 
              }}
            >
              <b>$</b> {ensureString(item.price, '0')}
            </Typography>
            <Typography 
              variant="subtitle1"
              sx={{ 
                mb: 1, 
                fontSize: { xs: '14px', sm: '16px' }, 
                fontWeight: '600',
                lineHeight: 1.2
              }}
              title={ensureString(item.name, 'Untitled')}
            >
              {truncateTitle(item.name, 25)}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1, 
                color: '#666',
                fontSize: { xs: '12px', sm: '14px' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {ensureString(item.description, 'No description')}
            </Typography>
            
            {renderSubcategories(item.subcategories)}
          </Box>
        </div>
      </div>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      overflow: 'hidden',
      mb: 2
    }}>
      <Box sx={{ 
        position: 'relative', 
        width: { xs: '100%', sm: '200px' }, 
        height: { xs: '200px', sm: 'auto' },
        flexShrink: 0 
      }}>
        <div className="product-img">
          <OptimizedImage item={item} mode={mode} onImageClick={handleCardClick} />
          
          <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
            <WishlistButton item={item} onToggle={onWishlistToggle} />
          </div>
          
          <span className={`badge ${getStatusColor(item.status)}`} style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {ensureString(item.status, 'Unknown')}
          </span>
        </div>
      </Box>
      
      <Box onClick={handleCardClick} sx={{ bgcolor: '#ffffff', flex: 1, p: { xs: 2, sm: 2 }, cursor: 'pointer', }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'flex-start' }, 
          mb: 2 
        }}>
          <Typography
            variant="h6"
            onClick={handleCardClick}
            sx={{ 
              cursor: 'pointer', 
              m: 0, 
              fontSize: { xs: '16px', sm: '18px' }, 
              fontWeight: '600',
              mb: { xs: 1, sm: 0 }
            }}
            title={ensureString(item.name, 'Untitled')}
          >
            {truncateTitle(item.name, 35)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '11px', sm: '12px' }
              }}
            >
              Posted: {ensureString(item.formattedDate, 'N/A')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Visibility sx={{ fontSize: '12px', color: '#666' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: { xs: '11px', sm: '12px' } }}>
                {ensureString(item.views || item.view_count || '0', '0')} views
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#666', 
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '12px', sm: '14px' },
          }}
        >
          {ensureString(item.description, 'No description')}
        </Typography>
        
        <Box className="ads-grid" sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: { xs: 1, sm: 2 }, 
          alignItems: 'center',
          '& > div': {
            fontSize: { xs: '12px', sm: '14px' }
          }
        }}>
          <Box>
           <b> Price :</b> <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>${ensureString(item.price, '0')}</span>
          </Box>
          <Box>
           <b> Category :</b> {ensureString(item.category, 'Unknown')}
          </Box>
          <Box>
            <b>Condition :</b> {ensureString(item.condition, 'Unknown')}
          </Box>
          {item.subcategories && item.subcategories.length > 0 && (
            <Box>
              <b>Subcategories :</b>
              {renderSubcategories(item.subcategories)}
            </Box>
          )}
          <Box>
            <b>Shipping :</b> {ensureString(item.shipping, 'Unknown')}
          </Box>
          <Box>
            <PaymentMethodIcon 
              method={item.payment_method} 
              paypalId={item.paypal_id}
              venmoId={item.venmo_id}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

const MarketPlaceTab = () => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [mode, setMode] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [subCategories, setSubCategories] = useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMarketplaceItems, setTotalMarketplaceItems] = useState(0);
  
  // Filter state structure - changed sub_category_id to handle single selection
  const [filters, setFilters] = useState({
    category_id: [],
    sub_category_id: null, // Changed to single value instead of array
    starting_budget: null,
    ending_budget: null,
    date_posted: null,
    condition: [],
    payment_method: []
  });
  
  const [pendingFilters, setPendingFilters] = useState({
    category_id: [],
    sub_category_id: null, // Changed to single value instead of array
    starting_budget: null,
    ending_budget: null,
    date_posted: null,
    condition: [],
    payment_method: []
  });
  
  const [hasFilterChanges, setHasFilterChanges] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isApplyingFiltersRef = useRef(false);
  const initializationRef = useRef(false);
  
  const validDateValues = useMemo(() => ['1', '2', '3'], []);
  const validConditionValues = useMemo(() => ['1', '2', '3', '4'], []);
  const validPaymentValues = useMemo(() => ['1', '2', '3'], []);
  
  const sortOptions = useMemo(() => [
    { value: '', label: 'Default' },
    { value: 'price_low_high', label: 'Price - Low to High' },
    { value: 'price_high_low', label: 'Price - High to Low' },
    { value: 'newest_oldest', label: 'Newest to Oldest' },
    { value: 'oldest_newest', label: 'Oldest to Newest' },
    { value: 'most_viewed', label: 'Most Viewed' },
    { value: 'alphabetical', label: 'Alphabetical' },
  ], []);
  
  const getUserId = useCallback(() => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.id || payload.sub;
      }
      return null;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }, []);
  
  const shippingMethodMap = useMemo(() => ({
    '1': "Cash On Delivery",
    '2': "Free Shipping", 
    '3': "Express Shipping",
  }), []);
  
  const paymentMethodMap = useMemo(() => ({
    '1': "cash",
    '2': "venmo", 
    '3': "paypal",
  }), []);
  
  const conditionMap = useMemo(() => ({
    '1': "New",
    '2': "Used - Like New",
    '3': "Used - Good", 
    '4': "Used - Fair",
  }), []);
  
  const conditionOptions = useMemo(() => [
    { value: '1', label: "New" },
    { value: '2', label: "Used - Like New" },
    { value: '3', label: "Used - Good" },
    { value: '4', label: "Used - Fair" },
  ], []);
  
  const datePostedOptions = useMemo(() => [
    { value: '1', label: "Today" },
    { value: '2', label: "This week" },
    { value: '3', label: "This month" },
  ], []);

  const paymentOptions = useMemo(() => [
    { value: '1', label: "Cash" },
    { value: '2', label: "Venmo" },
    { value: '3', label: "PayPal" },
  ], []);
  
  const itemsPerPageOptions = useMemo(() => [
    { value: 12, label: "12 per page" },
    { value: 15, label: "15 per page" },
    { value: 24, label: "24 per page" },
    { value: 48, label: "48 per page" },
  ], []);
  
  // Check if any filter has selected values
  const hasActiveFilters = useCallback((filterObj) => {
    return Object.entries(filterObj).some(([key, value]) => {
      if (value === null || value === undefined) {
        return false;
      }
      
      // For arrays, check if they have any items
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      
      if (key === 'starting_budget' && value === 0) {
        return false;
      }
      if (key === 'ending_budget' && value === 10000) {
        return false;
      }
      
      return value !== '';
    });
  }, []);

  // Modified filter comparison for arrays
  const compareFilters = useCallback((pendingFilters, currentFilters) => {
    return Object.keys(pendingFilters).some(key => {
      const pendingValue = pendingFilters[key];
      const currentValue = currentFilters[key];
      
      // For arrays, compare lengths and contents
      if (Array.isArray(pendingValue) && Array.isArray(currentValue)) {
        return pendingValue.length !== currentValue.length || 
               !pendingValue.every(val => currentValue.includes(val));
      }
      
      return pendingValue !== currentValue;
    });
  }, []);
  
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case "Published":
      case "Active":
        return "bg-success";
      case "Draft":
        return "bg-secondary";
      case "Sold":
        return "bg-warning";
      default:
        return "bg-dark";
    }
  }, []);
  
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Date formatting error:', error);
      return "N/A";
    }
  }, []);
  
  // Modified to handle mixed data types in URL params
  const updateURLParams = useCallback((newFilters, newSearchTerm, newSortBy) => {
    const params = new URLSearchParams();
    
    if (newSearchTerm && newSearchTerm.trim()) {
      params.set('search', newSearchTerm.trim());
    }
    
    if (newSortBy) {
      params.set('sort', newSortBy);
    }
    
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Handle arrays (multi-select filters)
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(','));
        } 
        // Handle single values
        else if (!Array.isArray(value) && value !== '' && value !== 0) {
          if (key === 'starting_budget' && value !== 0) {
            params.set(key, value);
          } else if (key === 'ending_budget' && value !== 10000) {
            params.set(key, value);
          } else if (!['starting_budget', 'ending_budget'].includes(key)) {
            params.set(key, value);
          }
        }
      }
    });
    
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);
  
  const saveFiltersToStorage = useCallback((filterData) => {
    try {
      localStorage.setItem('marketplaceFilters', JSON.stringify(filterData));
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error);
    }
  }, []);
  
  const loadFiltersFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem('marketplaceFilters');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load filters from localStorage:', error);
    }
    return null;
  }, []);
  
  const getMarketplaceAds = useCallback(async (page = 1, limit = itemsPerPage) => {
    try {
      console.log('📡 getMarketplaceAds called:', { page, limit });
      setLoading(true);
      setError(null);
      
      const params = {
        page: parseInt(page),
        limit: parseInt(limit)
      };
      
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads`,
        { 
          params,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
          }
        }
      );
      
      const data = response?.data?.data || {};
      const ads = data.ads || [];
      const pagination = data.pagination || {};
      
      setTotalItems(pagination.total || 0);
      setTotalPages(pagination.total_pages || Math.ceil((pagination.total || 0) / limit));
      setCurrentPage(pagination.current_page || page);
      setTotalMarketplaceItems(pagination.total || 0);
      
      const enriched = ads.map((ad) => {
        let subcategories = [];
        if (ad.subcategories) {
          if (Array.isArray(ad.subcategories)) {
            subcategories = ad.subcategories.map(sc =>
              typeof sc === 'string' ? sc :
                sc.name || sc.title || sc.id || 'Unnamed Subcategory'
            );
          } else if (typeof ad.subcategories === 'string') {
            subcategories = [ad.subcategories];
          } else if (typeof ad.subcategories === 'object') {
            subcategories = [ad.subcategories.name || ad.subcategories.title || 'Unnamed Subcategory'];
          }
        }
        
        return {
          ...ad,
          id: ad.id || ad.ad_id,
          name: ensureString(ad.name || ad.title, 'Untitled'),
          description: ensureString(ad.description, 'No description'),
          price: ensureString(ad.price, '0'),
          category: ensureString(ad.category_name || ad.category, 'Unknown'),
          shipping: ensureString(shippingMethodMap[String(ad.shipping_method || ad.shipping_option)], 'Unknown'),
          payment_method: ensureString(paymentMethodMap[String(ad.payment_methods || ad.payment_method)], 'cash'),
          condition: ensureString(conditionMap[String(ad.condition)], 'Unknown'),
          paypal_id: ensureString(ad.paypal_id),
          venmo_id: ensureString(ad.venmo_id),
          views: ad.views || ad.view_count || 0,
          imageUrl: extractImageUrl(ad),
          status: ensureString(ad.is_sold ? "Sold" : (ad.is_draft === 1 ? "Draft" : "Active"), 'Unknown'),
          formattedDate: ensureString(formatDate(ad.created_at || ad.posted_on), 'N/A'),
          subcategories: subcategories,
          is_wishlisted: ad.is_wishlisted || false
        };
      });
      
      setItems(enriched);
      
    } catch (err) {
      console.error("API error:", err);
      setError("Failed to load marketplace ads. Please try again.");
      toast.error("Failed to load marketplace ads");
      
      setTotalItems(0);
      setTotalPages(0);
      setCurrentPage(1);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [shippingMethodMap, paymentMethodMap, conditionMap, formatDate, itemsPerPage]);
  
  const getFilterData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      let response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/filter`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
          },
        }
      );
      
      if (response.status === 404) {
        response = await axiosInstance.post(
          `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/filter`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            },
          }
        );
      }
      
      const data = response?.data?.data || {};
      const categories = data.categories || [
        { id: 1, name: "Electronics", ads_count: 0 },
        { id: 2, name: "Books", ads_count: 0 },
        { id: 3, name: "Fashion", ads_count: 0 },
      ];
      
      const totalCount = categories.reduce((sum, category) => sum + (category.ads_count || 0), 0);
      setTotalMarketplaceItems(totalCount);
      
      setCategories(categories);
      setSubCategories(data.subcategories || []);
      
      const defaultRange = [0, 10000];
      setPriceRange(defaultRange);
      
    } catch (err) {
      console.error("Filter API error:", err);
      setCategories([
        { id: 1, name: "Electronics", ads_count: 3 },
        { id: 2, name: "Books", ads_count: 5 },
        { id: 3, name: "Fashion", ads_count: 2 },
      ]);
      setTotalMarketplaceItems(10);
      setPriceRange([0, 10000]);
    }
  }, []);
  
  // FIXED: Updated fetchFilteredItems to handle sub_category_id as single integer
  const fetchFilteredItems = useCallback(async (filterParams, page = 1, limit = itemsPerPage, searchQuery = '', sortQuery = '') => {
    try {
      console.log('🔍 fetchFilteredItems called:', { page, searchQuery, sortQuery, filterParams });
      setLoading(true);
      setError(null);
      
      const requestBody = {};
      
      const userId = getUserId();
      if (userId) {
        requestBody.id = userId;
      }
      
      if (searchQuery && searchQuery.trim()) {
        requestBody.search = searchQuery.trim();
      }
      
      if (sortQuery) {
        requestBody.sort_by = sortQuery;
      }
      
      // Handle category_id as comma-separated string (for multi-select)
      if (Array.isArray(filterParams.category_id) && filterParams.category_id.length > 0) {
        requestBody.category_id = filterParams.category_id.join(',');
      }
      
      // FIXED: Handle sub_category_id as single integer
      if (filterParams.sub_category_id !== null && filterParams.sub_category_id !== undefined) {
        requestBody.sub_category_id = parseInt(filterParams.sub_category_id);
      }
      
      if (filterParams.starting_budget !== null && 
          filterParams.starting_budget !== undefined && 
          filterParams.starting_budget !== 0) {
        requestBody.starting_budget = parseInt(filterParams.starting_budget);
      }
      
      if (filterParams.ending_budget !== null && 
          filterParams.ending_budget !== undefined && 
          filterParams.ending_budget !== 10000) {
        requestBody.ending_budget = parseInt(filterParams.ending_budget);
      }
      
      // date_posted as single integer
      if (filterParams.date_posted !== null && filterParams.date_posted !== undefined && filterParams.date_posted !== '') {
        requestBody.date_posted = parseInt(filterParams.date_posted);
      }
      
      if (Array.isArray(filterParams.condition) && filterParams.condition.length > 0) {
        requestBody.condition = filterParams.condition.join(',');
      }
      
      if (Array.isArray(filterParams.payment_method) && filterParams.payment_method.length > 0) {
        requestBody.payment_method = filterParams.payment_method.join(',');
      }
      
      requestBody.page = parseInt(page);
      requestBody.limit = parseInt(limit);
      
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/filter`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            "Content-Type": "application/json"
          },
        }
      );
      
      const data = response?.data?.data || {};
      const ads = data.ads || [];
      const pagination = data.pagination || {};
      
      setTotalItems(pagination.total || 0);
      setTotalPages(pagination.total_pages || Math.ceil((pagination.total || 0) / limit));
      setCurrentPage(pagination.current_page || page);
      
      const enriched = ads.map((ad) => {
        return {
          ...ad,
          id: ad.id || ad.ad_id,
          name: ensureString(ad.name || ad.title, 'Untitled'),
          description: ensureString(ad.description, 'No description'),
          price: ensureString(ad.price, '0'),
          category: ensureString(ad.category_name || ad.category, 'Unknown'),
          shipping: ensureString(shippingMethodMap[ad.shipping_method || ad.shipping_option], 'Unknown'),
          payment_method: ensureString(paymentMethodMap[ad.payment_methods || ad.payment_method], 'cash'),
          condition: ensureString(conditionMap[ad.condition], 'Unknown'),
          paypal_id: ensureString(ad.paypal_id),
          venmo_id: ensureString(ad.venmo_id),
          views: ad.views || ad.view_count || 0,
          imageUrl: extractImageUrl(ad),
          status: ensureString(ad.is_sold ? "Sold" : (ad.is_draft === 1 ? "Draft" : "Active"), 'Unknown'),
          formattedDate: ensureString(formatDate(ad.created_at || ad.posted_on), 'N/A'),
          subcategories: ad.subcategories || [],
          is_wishlisted: ad.is_wishlisted || false
        };
      });
      
      setItems(enriched);
      
    } catch (err) {
      console.error("Filter apply error:", err);
      
      if (err.response?.status === 400 && err.response?.data?.errors) {
        const errors = err.response.data.errors;
        let errorMessages = '';
        
        if (Array.isArray(errors)) {
          errorMessages = errors.join(', ');
        } else if (typeof errors === 'object') {
          errorMessages = Object.entries(errors)
            .map(([field, messages]) => {
              const messageText = Array.isArray(messages) 
                ? messages.join(', ') 
                : typeof messages === 'string' 
                  ? messages 
                  : String(messages);
              return `${field}: ${messageText}`;
            })
            .join('\n');
        } else {
          errorMessages = String(errors);
        }
        
        console.log(`Validation errors: ${errorMessages}`);
        toast.error(`Validation errors: ${errorMessages}`);
      } else {
        console.log("Failed to apply filters. Please try again.");
        toast.error("Failed to apply filters. Please try again.");
      }
      
      setTotalItems(0);
      setTotalPages(0);
      setCurrentPage(1);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [shippingMethodMap, paymentMethodMap, conditionMap, formatDate, itemsPerPage, getUserId]);
  
  const debouncedApplyFilters = useCallback(
    debounce(async (filterParams, searchQuery, sortQuery) => {
      // Prevent multiple simultaneous calls
      if (isApplyingFiltersRef.current) {
        return;
      }

      try {
        isApplyingFiltersRef.current = true;
        setLoading(true);
        setCurrentPage(1);
        
        if (hasActiveFilters(filterParams) || searchQuery || sortQuery) {
          await fetchFilteredItems(filterParams, 1, itemsPerPage, searchQuery, sortQuery);
        } else {
          await getMarketplaceAds(1, itemsPerPage);
        }
        
        // Only update URL if not in initialization phase
        if (!initializationRef.current) {
          updateURLParams(filterParams, searchQuery, sortQuery);
        }
      } catch (error) {
        console.error('Error in debouncedApplyFilters:', error);
        toast.error('Failed to apply filters');
      } finally {
        setLoading(false);
        isApplyingFiltersRef.current = false;
      }
    }, 300),
    [fetchFilteredItems, getMarketplaceAds, itemsPerPage, hasActiveFilters, updateURLParams]
  );
  
  const applyFilters = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isApplyingFiltersRef.current) {
      console.log('🚫 applyFilters blocked - already applying');
      return;
    }

    console.log('✅ applyFilters called');
    try {
      isApplyingFiltersRef.current = true;
      
      // Consolidate all state updates into a single batch
      setLoading(true);
      setFilters(pendingFilters);
      setHasFilterChanges(false);
      setCurrentPage(1);
      
      // Save to storage (synchronous)
      saveFiltersToStorage(pendingFilters);
      
      // Perform API call
      if (hasActiveFilters(pendingFilters) || searchTerm || sortBy) {
        await fetchFilteredItems(pendingFilters, 1, itemsPerPage, searchTerm, sortBy);
      } else {
        await getMarketplaceAds(1, itemsPerPage);
      }
      
      // Update URL (this might trigger useEffect, but we'll handle it)
      updateURLParams(pendingFilters, searchTerm, sortBy);
      
    } catch (error) {
      console.error('Error applying filters:', error);
      toast.error('Failed to apply filters');
    } finally {
      setLoading(false);
      isApplyingFiltersRef.current = false;
    }
  }, [pendingFilters, fetchFilteredItems, getMarketplaceAds, itemsPerPage, hasActiveFilters, searchTerm, sortBy, saveFiltersToStorage, updateURLParams]);
  
  // Reset filters with correct data types
  const resetFilters = useCallback(async () => {
    const resetFiltersState = {
      category_id: [],
      sub_category_id: null, // Changed to null instead of array
      starting_budget: null,
      ending_budget: null,
      date_posted: null,
      condition: [],
      payment_method: []
    };
    
    try {
      setLoading(true);
      
      setFilters(resetFiltersState);
      setPendingFilters(resetFiltersState);
      setPriceRange([0, 10000]);
      setSubCategories([]);
      setCurrentPage(1);
      setError(null);
      setHasFilterChanges(false);
      setSearchTerm('');
      setSortBy('');
      
      localStorage.removeItem('marketplaceFilters');
      setSearchParams({}, { replace: true });
      
      await getMarketplaceAds(1, itemsPerPage);
    } catch (error) {
      console.error('Error resetting filters:', error);
      toast.error('Failed to reset filters');
    } finally {
      setLoading(false);
    }
  }, [getMarketplaceAds, itemsPerPage, setSearchParams]);
  
  // Clear filter functions
  const clearCategoryFilter = useCallback(() => {
    const updatedFilters = {
      ...pendingFilters,
      category_id: [],
      sub_category_id: null // Reset to null instead of array
    };
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
    setSubCategories([]);
  }, [pendingFilters]);
  
  const clearPriceFilter = useCallback(() => {
    setPriceRange([0, 10000]);
    const updatedFilters = {
      ...pendingFilters,
      starting_budget: null,
      ending_budget: null
    };
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);
  
  const clearDateFilter = useCallback(() => {
    const updatedFilters = {
      ...pendingFilters,
      date_posted: null
    };
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);
  
  const clearConditionFilter = useCallback(() => {
    const updatedFilters = {
      ...pendingFilters,
      condition: []
    };
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);

  const clearPaymentFilter = useCallback(() => {
    const updatedFilters = {
      ...pendingFilters,
      payment_method: []
    };
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);
  
  const handlePageChange = useCallback((event, newPage) => {
    if (newPage === currentPage || isApplyingFiltersRef.current) {
      return;
    }
    
    setCurrentPage(newPage);
    
    if (hasActiveFilters(filters) || searchTerm || sortBy) {
      fetchFilteredItems(filters, newPage, itemsPerPage, searchTerm, sortBy);
    } else {
      getMarketplaceAds(newPage, itemsPerPage);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [filters, fetchFilteredItems, getMarketplaceAds, itemsPerPage, currentPage, hasActiveFilters, searchTerm, sortBy]);
  
  const handleItemsPerPageChange = useCallback((event) => {
    if (isApplyingFiltersRef.current) {
      return;
    }
    
    const newItemsPerPage = parseInt(event.target.value);
    
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    if (hasActiveFilters(filters) || searchTerm || sortBy) {
      fetchFilteredItems(filters, 1, newItemsPerPage, searchTerm, sortBy);
    } else {
      getMarketplaceAds(1, newItemsPerPage);
    }
  }, [filters, fetchFilteredItems, getMarketplaceAds, hasActiveFilters, searchTerm, sortBy]);
  
  const handleSortChange = useCallback((event) => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    
    // Prevent multiple calls
    if (isApplyingFiltersRef.current) {
      return;
    }
    
    // Use current filters state, not pending
    if (hasActiveFilters(filters) || searchTerm || newSortBy) {
      debouncedApplyFilters(filters, searchTerm, newSortBy);
    } else {
      debouncedApplyFilters({}, '', newSortBy);
    }
  }, [filters, searchTerm, hasActiveFilters, debouncedApplyFilters]);
  
  const handleSearchChange = useCallback((event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    
    // Prevent multiple calls
    if (isApplyingFiltersRef.current) {
      return;
    }
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      // Use current filters state, not pending
      if (hasActiveFilters(filters) || newSearchTerm.trim() || sortBy) {
        debouncedApplyFilters(filters, newSearchTerm, sortBy);
      } else {
        debouncedApplyFilters({}, newSearchTerm, '');
      }
    }, 500);
  }, [filters, sortBy, hasActiveFilters, debouncedApplyFilters]);
  
  const toggleWishlist = useCallback(async (adId, newStatus) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Ocp-Apim-Subscription-Key': '6476f278e86f4fc6a4643077bc489c05'
      };
      
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist`,
        { 
          ad_id: Number(adId),
          is_wishlisted: newStatus 
        },
        { headers }
      );
      
      toast.success(newStatus ? "Added to wishlist!" : "Removed from wishlist!");
      
      setItems(prev =>
        prev.map(item =>
          item.id === adId
            ? { ...item, is_wishlisted: newStatus }
            : item
        )
      );
    } catch (err) {
      console.error('Wishlist error:', err);
      toast.error("Failed to update wishlist");
      throw err;
    }
  }, []);
  
  const handlePriceChange = useCallback((event, newValue) => {
    setPriceRange(newValue);
    
    const updatedFilters = {
      ...pendingFilters,
      starting_budget: newValue[0] === 0 ? null : newValue[0],
      ending_budget: newValue[1] === 10000 ? null : newValue[1]
    };
    
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);
  
  const handlePriceInputChange = useCallback((type, value) => {
    const numValue = value === '' ? (type === 'min' ? 0 : 10000) : parseInt(value) || 0;
    
    let newRange;
    if (type === 'min') {
      newRange = [Math.min(numValue, priceRange[1]), priceRange[1]];
    } else {
      newRange = [priceRange[0], Math.max(numValue, priceRange[0])];
    }
    
    setPriceRange(newRange);
    
    const updatedFilters = {
      ...pendingFilters,
      starting_budget: newRange[0] === 0 ? null : newRange[0],
      ending_budget: newRange[1] === 10000 ? null : newRange[1]
    };
    
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters, priceRange]);
  
  const getSubCategories = useCallback(async (categoryId) => {
    try {
      setSubCategories([]);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/subcategories`,
        { category_id: categoryId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
          },
        }
      );
      const responseData = response.data?.data || [];
      let subcats = [];
      if (Array.isArray(responseData)) {
        subcats = responseData;
      } else if (responseData && typeof responseData === "object") {
        subcats = Object.values(responseData);
      }
      setSubCategories(subcats);
    } catch (error) {
      console.error("Subcategory fetch error:", error);
      setSubCategories([]);
    }
  }, []);
  
  // Handle multiple category selection
  const handleCategoryChange = useCallback(async (event) => {
    const { target: { value } } = event;
    const selectedCategories = typeof value === 'string' ? value.split(',') : value;
    
    const updatedFilters = {
      ...pendingFilters,
      category_id: selectedCategories.map(id => parseInt(id)).filter(id => !isNaN(id)),
      sub_category_id: null // Reset to null when categories change
    };
    
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
    
    // Load subcategories for the first selected category (or clear if none selected)
    if (selectedCategories.length > 0 && selectedCategories[0]) {
      await getSubCategories(parseInt(selectedCategories[0]));
    } else {
      setSubCategories([]);
    }
  }, [pendingFilters, getSubCategories]);
  
  // FIXED: Handle single subcategory selection
  const handleSubcategoryChange = useCallback((event) => {
    const selectedValue = event.target.value;
    
    const updatedFilters = {
      ...pendingFilters,
      sub_category_id: selectedValue ? parseInt(selectedValue) : null
    };
    
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);
  
  // Handle multiple condition selection with checkboxes
  const handleConditionChange = useCallback((event) => {
    const { target: { value, checked } } = event;
    
    const updatedConditions = checked
      ? [...pendingFilters.condition, value]
      : pendingFilters.condition.filter(condition => condition !== value);
    
    const updatedFilters = {
      ...pendingFilters,
      condition: updatedConditions
    };
    
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);
  
  // Handle single date posted selection
  const handleDatePostedChange = useCallback((event) => {
    const selectedValue = event.target.value;
    
    const updatedFilters = {
      ...pendingFilters,
      date_posted: selectedValue === '' ? null : parseInt(selectedValue)
    };
    
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);

  // Handle multiple payment method selection
  const handlePaymentMethodChange = useCallback((event) => {
    const { target: { value, checked } } = event;
    
    const updatedPaymentMethods = checked
      ? [...pendingFilters.payment_method, value]
      : pendingFilters.payment_method.filter(method => method !== value);
    
    const updatedFilters = {
      ...pendingFilters,
      payment_method: updatedPaymentMethods
    };
    
    setPendingFilters(updatedFilters);
    setHasFilterChanges(true);
  }, [pendingFilters]);
  
  const renderSubcategories = useCallback((subcategories) => {
    if (!subcategories || subcategories.length === 0) {
      return null;
    }
    return (
      <Box sx={{ mt: 1 }}>
        {subcategories.map((subcat, index) => (
          <Chip
            key={index}
            label={ensureString(subcat, `Subcategory ${index + 1}`)}
            size="small"
            sx={{ 
              mr: 0.5, 
              mb: 0.5,
              fontSize: { xs: '0.6rem', sm: '0.75rem' },
              height: { xs: '20px', sm: '24px' }
            }}
            variant="outlined"
          />
        ))}
      </Box>
    );
  }, []);
  
  const handleItemClick = useCallback((itemId) => {
    navigate(`/marketplace/${itemId}`);
  }, [navigate]);

  // useEffect hooks
  useEffect(() => {
    // Skip during initialization or when applying filters
    if (initializationRef.current || isApplyingFiltersRef.current) {
      return;
    }
    
    const hasChanges = compareFilters(pendingFilters, filters);
    setHasFilterChanges(hasChanges);
  }, [pendingFilters, filters, compareFilters]);
  
  // Initialization with array support - prevent multiple calls
  useEffect(() => {
    // Skip if already initializing or applying filters
    if (initializationRef.current || isApplyingFiltersRef.current) {
      return;
    }

    const initializeFilters = async () => {
      try {
        initializationRef.current = true;
        
        const categoryFromUrl = searchParams.get('category');
        const searchFromUrl = searchParams.get('search');
        const sortFromUrl = searchParams.get('sort');
        
        const savedFilters = loadFiltersFromStorage();
        
        let initialFilters = {
          category_id: [],
          sub_category_id: null, // Changed to null
          starting_budget: null,
          ending_budget: null,
          date_posted: null,
          condition: [],
          payment_method: []
        };
        
        if (categoryFromUrl) {
          initialFilters.category_id = [parseInt(categoryFromUrl)];
        } else if (savedFilters) {
          initialFilters = { ...initialFilters, ...savedFilters };
        }
        
        if (searchFromUrl) {
          setSearchTerm(searchFromUrl);
        }
        
        if (sortFromUrl) {
          setSortBy(sortFromUrl);
        }
        
        // Parse URL params
        const arrayFilters = [
          'category_id', 'condition', 'payment_method'
        ];
        
        const singleValueFilters = [
          'date_posted', 'sub_category_id'
        ];
        
        arrayFilters.forEach(filterKey => {
          const urlValue = searchParams.get(filterKey);
          if (urlValue) {
            if (filterKey === 'category_id') {
              initialFilters[filterKey] = urlValue.split(',').map(id => parseInt(id)).filter(id => !isNaN(id));
            } else {
              initialFilters[filterKey] = urlValue.split(',').filter(val => val.trim());
            }
          }
        });

        singleValueFilters.forEach(filterKey => {
          const urlValue = searchParams.get(filterKey);
          if (urlValue) {
            initialFilters[filterKey] = parseInt(urlValue);
          }
        });
        
        // Handle budget filters separately
        const budgetFilters = ['starting_budget', 'ending_budget'];
        budgetFilters.forEach(filterKey => {
          const urlValue = searchParams.get(filterKey);
          if (urlValue) {
            initialFilters[filterKey] = parseInt(urlValue);
          }
        });
        
        // Batch state updates
        setFilters(initialFilters);
        setPendingFilters(initialFilters);
        
        if (initialFilters.starting_budget || initialFilters.ending_budget) {
          setPriceRange([
            initialFilters.starting_budget || 0,
            initialFilters.ending_budget || 10000
          ]);
        }
        
        if (initialFilters.category_id.length > 0) {
          await getSubCategories(initialFilters.category_id[0]);
        }
        
        await getFilterData();
        
        // Single API call for initialization
        if (hasActiveFilters(initialFilters) || searchFromUrl || sortFromUrl) {
          await fetchFilteredItems(initialFilters, 1, itemsPerPage, searchFromUrl || '', sortFromUrl || '');
        } else {
          await getMarketplaceAds(1, itemsPerPage);
        }
        
      } catch (error) {
        console.error('Error during initialization:', error);
        toast.error('Failed to initialize filters');
      } finally {
        initializationRef.current = false;
      }
    };

    initializeFilters();
  }, []); // Empty dependency array - only run once on mount
  
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Reset refs on cleanup
      isApplyingFiltersRef.current = false;
      initializationRef.current = false;
    };
  }, []);
  
  // Mobile Filter Component
  const MobileFilterDrawer = () => (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1300,
      display: { xs: mobileFilterOpen ? 'flex' : 'none', md: 'none' }
    }}>
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        maxHeight: '85vh',
        overflow: 'auto',
        p: 3
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          pb: 2,
          borderBottom: '2px solid #f0f0f0'
        }}>
          <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 600, color: '#1976d2' }}>
            Filters
          </Typography>
          <IconButton 
            onClick={() => setMobileFilterOpen(false)} 
            sx={{ 
              p: 1,
              bgcolor: '#f5f5f5',
              '&:hover': { bgcolor: '#e0e0e0' }
            }}
          >
            <Close />
          </IconButton>
        </Box>
        
        {/* Categories */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
            Categories
          </Typography>
          <FormControl fullWidth size="medium">
            <Select
              multiple
              value={pendingFilters.category_id}
              onChange={handleCategoryChange}
              displayEmpty
              sx={{
                borderRadius: '12px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return 'All Categories';
                }
                const selectedNames = selected.map(id => {
                  const category = categories.find(cat => cat.id === parseInt(id));
                  return category ? category.name : id;
                });
                return selectedNames.join(', ');
              }}
            >
              <MenuItem disabled value="">
                <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
                  All Categories ({totalMarketplaceItems})
                </Typography>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id} sx={{ py: 1 }}>
                  <Checkbox 
                    checked={pendingFilters.category_id.indexOf(category.id) > -1} 
                    sx={{ mr: 1 }}
                  />
                  <Typography>
                    {ensureString(category.name, 'Unknown Category')} ({ensureString(category.ads_count, 0)})
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {pendingFilters.category_id.length > 0 && subCategories.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 1 }}>
                Subcategories
              </Typography>
              <FormControl fullWidth size="medium">
                <Select
                  value={pendingFilters.sub_category_id || ''}
                  onChange={handleSubcategoryChange}
                  displayEmpty
                  sx={{
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                >
                  <MenuItem value="">
                    <Typography sx={{ color: '#666', fontStyle: 'italic' }}>
                      All Subcategories
                    </Typography>
                  </MenuItem>
                  {subCategories.map((subcat) => (
                    <MenuItem key={subcat.id} value={subcat.id} sx={{ py: 1 }}>
                      <Typography>
                        {ensureString(subcat.name || subcat.title, `Subcategory ${subcat.id}`)}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
        
        {/* Price Range */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
            Price Range
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Min Price"
              type="number"
              size="medium"
              value={priceRange[0]}
              onChange={(e) => handlePriceInputChange('min', e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ color: '#666', mr: 1, fontWeight: 500 }}>$</Typography>,
              }}
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
            />
            <TextField
              label="Max Price"
              type="number"
              size="medium"
              value={priceRange[1]}
              onChange={(e) => handlePriceInputChange('max', e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ color: '#666', mr: 1, fontWeight: 500 }}>$</Typography>,
              }}
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px'
                }
              }}
            />
          </Box>
          <Box sx={{ px: 2 }}>
            <Slider
              value={priceRange}
              onChange={handlePriceChange}
              valueLabelDisplay="auto"
              min={0}
              max={10000}
              step={10}
              sx={{ 
                color: '#1976d2',
                '& .MuiSlider-thumb': {
                  width: 20,
                  height: 20
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666' }}>
                $0
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666' }}>
                $10,000+
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Date Posted */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
            Date Posted
          </Typography>
          <Box sx={{ 
            bgcolor: '#f8f9fa', 
            borderRadius: '12px', 
            p: 2,
            border: '1px solid #e3e6ea'
          }}>
            <RadioGroup
              value={pendingFilters.date_posted || ''}
              onChange={handleDatePostedChange}
            >
              <FormControlLabel
                value=""
                control={<Radio size="small" sx={{ color: '#1976d2' }} />}
                label="Any time"
                sx={{ 
                  display: 'block', 
                  mb: 1.5, 
                  '& .MuiFormControlLabel-label': { 
                    fontSize: '0.9rem',
                    fontWeight: 500
                  } 
                }}
              />
              {datePostedOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" sx={{ color: '#1976d2' }} />}
                  label={option.label}
                  sx={{ 
                    display: 'block', 
                    mb: 1.5, 
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      fontWeight: 500
                    } 
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
        </Box>
        
        {/* Condition */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
            Condition
          </Typography>
          <Box sx={{ 
            bgcolor: '#f8f9fa', 
            borderRadius: '12px', 
            p: 2,
            border: '1px solid #e3e6ea'
          }}>
            <FormGroup>
              {conditionOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={pendingFilters.condition.includes(option.value)}
                      onChange={handleConditionChange}
                      value={option.value}
                      sx={{ color: '#1976d2' }}
                    />
                  }
                  label={option.label}
                  sx={{ 
                    display: 'block', 
                    mb: 1.5,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      fontWeight: 500
                    } 
                  }}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>

        {/* Payment Method */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
            Payment Method
          </Typography>
          <Box sx={{ 
            bgcolor: '#f8f9fa', 
            borderRadius: '12px', 
            p: 2,
            border: '1px solid #e3e6ea'
          }}>
            <FormGroup>
              {paymentOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={pendingFilters.payment_method.includes(option.value)}
                      onChange={handlePaymentMethodChange}
                      value={option.value}
                      sx={{ color: '#1976d2' }}
                    />
                  }
                  label={option.label}
                  sx={{ 
                    display: 'block', 
                    mb: 1.5,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      fontWeight: 500
                    } 
                  }}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
        
        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          pt: 2, 
          borderTop: '2px solid #f0f0f0',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'white',
          zIndex: 10
        }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={resetFilters}
            sx={{ 
              py: 1.5, 
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '12px',
              borderWidth: '2px',
              '&:hover': {
                borderWidth: '2px'
              }
            }}
          >
            Reset All
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              applyFilters();
              setMobileFilterOpen(false);
            }}
            disabled={!hasFilterChanges}
            sx={{ 
              py: 1.5, 
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(25,118,210,0.4)'
              }
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Box>
  );
  
  if (loading && items.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        p: 3
      }}>
        {loadingGif ? (
          <img
            src={loadingGif}
            alt="Loading..."
            style={{ width: '80px', display: 'inline-block' }}
          />
        ) : (
          <CircularProgress size={80} thickness={4} />
        )}
        <Typography variant="h6" sx={{ mt: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Loading marketplace ads...
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Box class="container">
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {/* Desktop Sidebar - Professional styling */}
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ 
              position: 'fixed',
              width: '25%',
              maxWidth: '300px',
              minWidth: '280px',
              zIndex: 100,
              height: '85vh',
              pr: 2,
            }}>
              <Paper elevation={2} sx={{ 
                p: 3,
                height: '550px',
                overflowY: 'auto',
                backgroundColor: '#fff',
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                borderRadius: '16px',
                border: '1px solid #f0f0f0',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#c1c1c1',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#a8a8a8',
                  },
                },
              }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 700,
                  color: '#1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <FilterList sx={{ fontSize: '1.2rem' }} />
                  Filters
                </Typography>
              </Box>
              
              {/* Categories */}
              <Accordion sx={{ 
                '&.MuiAccordion-root': { 
                  mb: 2,
                  boxShadow: 'none',
                  border: '1px solid #e3e6ea',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }, 
                '& .MuiAccordionSummary-root': { 
                  bgcolor: '#f8f9fa',
                  borderRadius: 0
                }, 
                '& .MuiAccordionDetails-root': { 
                
                } 
              }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#333' }}>
                    Categories
                  </Typography>
                  {pendingFilters.category_id.length > 0 && (
                    <Chip 
                      size="small" 
                      label={pendingFilters.category_id.length} 
                      color="primary" 
                      sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <FormControl fullWidth size="medium">
                    <Select
                      multiple
                      value={pendingFilters.category_id}
                      onChange={handleCategoryChange}
                      displayEmpty
                      sx={{
                        borderRadius: '10px',
                        bgcolor: '#fff',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px'
                        }
                      }}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <Typography sx={{ color: '#666' }}>All Categories</Typography>;
                        }
                        return <Typography>{selected.length} selected</Typography>;
                      }}
                    >
                      <MenuItem disabled value="" sx={{ py: 1.5 }}>
                        <Typography sx={{ color: '#666', fontStyle: 'italic', fontSize: '0.9rem' }}>
                          All Categories ({totalMarketplaceItems})
                        </Typography>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id} sx={{ py: 1.5 }}>
                          <Checkbox 
                            checked={pendingFilters.category_id.indexOf(category.id) > -1} 
                            sx={{ mr: 1.5, color: '#1976d2' }}
                          />
                          <Typography sx={{ fontSize: '0.9rem' }}>
                            {ensureString(category.name, 'Unknown Category')} ({ensureString(category.ads_count, 0)})
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {pendingFilters.category_id.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ 
                        fontSize: '0.85rem', 
                        fontWeight: 600, 
                        mb: 1.5,
                        color: '#555'
                      }}>
                        Subcategories
                      </Typography>
                      {subCategories.length > 0 ? (
                        <FormControl fullWidth size="medium">
                          <Select
                            value={pendingFilters.sub_category_id || ''}
                            onChange={handleSubcategoryChange}
                            displayEmpty
                            sx={{
                              borderRadius: '10px',
                              bgcolor: '#fff',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '10px'
                              }
                            }}
                          >
                            <MenuItem value="" sx={{ py: 1.5 }}>
                              <Typography sx={{ color: '#666', fontStyle: 'italic', fontSize: '0.9rem' }}>
                                All Subcategories
                              </Typography>
                            </MenuItem>
                            {subCategories.map((subcat) => (
                              <MenuItem key={subcat.id} value={subcat.id} sx={{ py: 1.5 }}>
                                <Typography sx={{ fontSize: '0.9rem' }}>
                                  {ensureString(subcat.name || subcat.title, `Subcategory ${subcat.id}`)}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ 
                          fontSize: '0.85rem',
                          fontStyle: 'italic',
                          textAlign: 'center',
                          py: 2,
                          bgcolor: '#f5f5f5',
                          borderRadius: '8px'
                        }}>
                          No subcategories available
                        </Typography>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              
              {/* Price Range */}
              <Accordion sx={{ 
                '&.MuiAccordion-root': { 
                  mb: 2,
                  boxShadow: 'none',
                  border: '1px solid #e3e6ea',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }, 
                '& .MuiAccordionSummary-root': { 
                  bgcolor: '#f8f9fa',
                  borderRadius: 0
                }, 
                '& .MuiAccordionDetails-root': { 
                
                } 
              }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#333' }}>
                    Price Range
                  </Typography>
                  {(priceRange[0] !== 0 || priceRange[1] !== 10000) && (
                    <Chip 
                      size="small" 
                      label={`$${priceRange[0]} - $${priceRange[1]}`}
                      color="primary" 
                      sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                      <TextField
                        label="Min Price"
                        type="number"
                        size="small"
                        value={priceRange[0]}
                        onChange={(e) => handlePriceInputChange('min', e.target.value)}
                        InputProps={{
                          startAdornment: <Typography sx={{ color: '#666', mr: 0.5, fontWeight: 600 }}>$</Typography>,
                        }}
                        sx={{ 
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: '#fff'
                          }
                        }}
                      />
                      <TextField
                        label="Max Price"
                        type="number"
                        size="small"
                        value={priceRange[1]}
                        onChange={(e) => handlePriceInputChange('max', e.target.value)}
                        InputProps={{
                          startAdornment: <Typography sx={{ color: '#666', mr: 0.5, fontWeight: 600 }}>$</Typography>,
                        }}
                        sx={{ 
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: '#fff'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ px: 1 }}>
                    <Slider
                      value={priceRange}
                      onChange={handlePriceChange}
                      valueLabelDisplay="auto"
                      min={0}
                      max={10000}
                      step={10}
                      sx={{ 
                        color: '#1976d2',
                        '& .MuiSlider-thumb': {
                          width: 18,
                          height: 18,
                          boxShadow: '0 2px 8px rgba(25,118,210,0.3)'
                        },
                        '& .MuiSlider-track': {
                          height: 6
                        },
                        '& .MuiSlider-rail': {
                          height: 6,
                          opacity: 0.3
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>
                        $0
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500 }}>
                        $10,000+
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              {/* Date Posted */}
              <Accordion sx={{ 
                '&.MuiAccordion-root': { 
                  mb: 2,
                  boxShadow: 'none',
                  border: '1px solid #e3e6ea',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }, 
                '& .MuiAccordionSummary-root': { 
                  bgcolor: '#f8f9fa',
                  borderRadius: 0
                }, 
                '& .MuiAccordionDetails-root': { 
                  px: 2
                } 
              }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#333' }}>
                    Date Posted
                  </Typography>
                  {pendingFilters.date_posted && (
                    <Chip 
                      size="small" 
                      label={datePostedOptions.find(opt => opt.value === String(pendingFilters.date_posted))?.label || "Selected"} 
                      color="primary" 
                      sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ 
                    bgcolor: '#f8f9fa', 
                    borderRadius: '10px', 
                    p: 2,
                    border: '1px solid #e3e6ea'
                  }}>
                    <RadioGroup
                      value={pendingFilters.date_posted || ''}
                      onChange={handleDatePostedChange}
                    >
                      <FormControlLabel
                        value=""
                        control={<Radio size="small" sx={{ color: '#1976d2' }} />}
                        label="Any time"
                        sx={{ 
                          display: 'block', 
                          mb: 1.5, 
                          '& .MuiFormControlLabel-label': { 
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            color: '#333'
                          } 
                        }}
                      />
                      {datePostedOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio size="small" sx={{ color: '#1976d2' }} />}
                          label={option.label}
                          sx={{ 
                            display: 'block', 
                            mb: 1.5, 
                            '& .MuiFormControlLabel-label': { 
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              color: '#333'
                            } 
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              {/* Condition */}
              <Accordion sx={{ 
                '&.MuiAccordion-root': { 
                  mb: 2,
                  boxShadow: 'none',
                  border: '1px solid #e3e6ea',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }, 
                '& .MuiAccordionSummary-root': { 
                  bgcolor: '#f8f9fa',
                  borderRadius: 0
                }, 
                '& .MuiAccordionDetails-root': { 
                  px: 2
                } 
              }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#333' }}>
                    Condition
                  </Typography>
                  {pendingFilters.condition.length > 0 && (
                    <Chip 
                      size="small" 
                      label={pendingFilters.condition.length} 
                      color="primary" 
                      sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ 
                    bgcolor: '#f8f9fa', 
                    borderRadius: '10px', 
                    p: 2,
                    border: '1px solid #e3e6ea'
                  }}>
                    <FormGroup>
                      {conditionOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              size="small"
                              checked={pendingFilters.condition.includes(option.value)}
                              onChange={handleConditionChange}
                              value={option.value}
                              sx={{ color: '#1976d2' }}
                            />
                          }
                          label={option.label}
                          sx={{ 
                            display: 'block', 
                            mb: 1.5,
                            '& .MuiFormControlLabel-label': { 
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              color: '#333'
                            } 
                          }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Payment Method */}
              <Accordion sx={{ 
                '&.MuiAccordion-root': { 
                  mb: 3,
                  boxShadow: 'none',
                  border: '1px solid #e3e6ea',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }, 
                '& .MuiAccordionSummary-root': { 
                  bgcolor: '#f8f9fa',
                  borderRadius: 0
                }, 
                '& .MuiAccordionDetails-root': { 
                  px: 2
                } 
              }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#333' }}>
                    Payment Method
                  </Typography>
                  {pendingFilters.payment_method.length > 0 && (
                    <Chip 
                      size="small" 
                      label={pendingFilters.payment_method.length} 
                      color="primary" 
                      sx={{ ml: 1, height: '20px', fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ 
                    bgcolor: '#f8f9fa', 
                    borderRadius: '10px', 
                    p: 2,
                    border: '1px solid #e3e6ea'
                  }}>
                    <FormGroup>
                      {paymentOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              size="small"
                              checked={pendingFilters.payment_method.includes(option.value)}
                              onChange={handlePaymentMethodChange}
                              value={option.value}
                              sx={{ color: '#1976d2' }}
                            />
                          }
                          label={option.label}
                          sx={{ 
                            display: 'block', 
                            mb: 1.5,
                            '& .MuiFormControlLabel-label': { 
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              color: '#333'
                            } 
                          }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1.5, 
                mt: 2, 
                pt: 3, 
                borderTop: '2px solid #f0f0f0'
              }}>
                <Button 
                  size="medium" 
                  variant="outlined" 
                  onClick={resetFilters}
                  fullWidth
                  sx={{ 
                    height: '44px', 
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    borderRadius: '10px',
                    borderWidth: '2px',
                    '&:hover': {
                      borderWidth: '2px',
                      bgcolor: '#f5f5f5'
                    }
                  }}
                >
                  Reset All
                </Button>
                <Button
                  size="medium"
                  variant="contained"
                  color="primary"
                  onClick={applyFilters}
                  disabled={!hasFilterChanges || loading || isApplyingFiltersRef.current}
                  fullWidth
                  sx={{ 
                    height: '44px', 
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25,118,210,0.4)'
                    },
                    '&:disabled': {
                      bgcolor: '#e0e0e0',
                      boxShadow: 'none'
                    }
                  }}
                >
                  Apply Filters
                </Button>
              </Box>
            </Paper>
            </Box>
          </Grid>
          
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {/* Mobile Filter Button */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                fullWidth
                onClick={() => setMobileFilterOpen(true)}
                sx={{ 
                  py: 2,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderWidth: '2px',
                  bgcolor: '#fff',
                  color: '#1976d2',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    borderWidth: '2px',
                    bgcolor: '#f8f9fa',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filters
                  </Typography>
                  {hasActiveFilters(pendingFilters) && (
                    <Chip 
                      size="small" 
                      label="Active" 
                      color="primary" 
                      sx={{ 
                        height: '24px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: '#1976d2',
                        color: 'white'
                      }}
                    />
                  )}
                </Box>
              </Button>
            </Box>
            
            {/* Search and Controls */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2
              }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search marketplace..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    size="medium"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                          borderWidth: '2px',
                        },
                        '& input': {
                          padding: '14px 0',
                          fontSize: '16px',
                          color: '#333',
                        },
                        '& input::placeholder': {
                          color: '#999',
                          opacity: 1,
                        }
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 4px 12px rgba(25,118,210,0.15)',
                        }
                      }
                    }}
                  />
                </Box>
              </Box>

              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 2 }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                    <Select
                      value={sortBy}
                      onChange={handleSortChange}
                      displayEmpty
                    >
                      {sortOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                    <Select
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      displayEmpty
                    >
                      {itemsPerPageOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                
              <Box sx={{ 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center',
  mb: 2,
  py: 1
}}>
  <Typography variant="body1" sx={{ 
    fontWeight: 400,
    color: '#666',
    marginTop: '15px',
    fontSize: { xs: '14px', sm: '14px' }
  }}>
    {loading ? 'Loading...' : (
      <>
        Showing {items.length} of {totalItems} total listings
        {searchTerm && ` for "${searchTerm}"`}
        {hasActiveFilters(filters) && ' (filtered)'}
      </>
    )}
  </Typography>
</Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  justifyContent: { xs: 'center', sm: 'flex-end' }
                }}>
                  <Button
                    variant={mode === 0 ? "contained" : "outlined"}
                    onClick={() => setMode(0)}
                    sx={{ minWidth: '48px', height: '40px' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M2 2H11V11.2657H2V2ZM4 4V9.26573H9V4H4Z" fill="currentColor" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M13 2H22V11H13V2ZM15 4V9H20V4H15Z" fill="currentColor" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M2 13H11V22H2V13ZM4 15V20H9V15H4Z" fill="currentColor" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M13 13H22V22H13V13ZM15 15V20H20V15H15Z" fill="currentColor" />
                    </svg>
                  </Button>
                  <Button
                    variant={mode === 1 ? "contained" : "outlined"}
                    onClick={() => setMode(1)}
                    sx={{ minWidth: '48px', height: '40px' }}
                  >
                    <svg viewBox="0 0 512 512" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M48 208C21.49 208 0 229.5 0 256s21.49 48 48 48S96 282.5 96 256S74.51 208 48 208zM48 368C21.49 368 0 389.5 0 416s21.49 48 48 48S96 442.5 96 416S74.51 368 48 368zM48 48C21.49 48 0 69.49 0 96s21.49 48 48 48S96 122.5 96 96S74.51 48 48 48zM192 128h288c17.67 0 32-14.33 32-31.1S497.7 64 480 64H192C174.3 64 160 78.33 160 95.1S174.3 128 192 128zM480 224H192C174.3 224 160 238.3 160 256s14.33 32 32 32h288c17.67 0 32-14.33 32-32S497.7 224 480 224zM480 384H192c-17.67 0-32 14.33-32 32s14.33 32 32 32h288c17.67 0 32-14.33 32-32S497.7 384 480 384z" fill="currentColor" />
                    </svg>
                  </Button>
                </Box>
              </Box>
            </Box>
            
            {/* Products Grid */}
            <Box>
              {loading && items.length > 0 && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading...
                  </Typography>
                </Box>
              )}
              
              {!loading && items.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: { xs: 4, sm: 6 },
                  px: 2
                }}>
                  <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    No items found matching your criteria
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Try adjusting your filters or search term
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={resetFilters}
                    sx={{ px: 3, py: 1 }}
                  >
                    Reset Filters
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {items.map((item) => (
                    <Grid 
                      item 
                      xs={mode === 0 ? 6 : 12} 
                      sm={mode === 0 ? 6 : 12} 
                      md={mode === 0 ? 4 : 12} 
                      key={item.id}
                    >
                      <ProductCard
                        item={item}
                        mode={mode}
                        onItemClick={handleItemClick}
                        onWishlistToggle={toggleWishlist}
                        getStatusColor={getStatusColor}
                        renderSubcategories={renderSubcategories}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
            
            {/* Pagination */}
            {!loading && items.length > 0 && totalPages > 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: { xs: 3, sm: 4 },
                px: 1
              }}>
                <Stack spacing={2} alignItems="center">
                  <Pagination
                    count={Math.max(Number(totalPages) || 1, 1)}
                    page={Math.min(Number(currentPage) || 1, Number(totalPages) || 1)}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    size="medium"
                    disabled={loading}
                    siblingCount={1}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minWidth: { xs: '28px', sm: '32px' },
                        height: { xs: '28px', sm: '32px' }
                      }
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ 
                      textAlign: 'center',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    Page {ensureString(currentPage, '1')} of {ensureString(totalPages, '1')} ({ensureString(totalItems, '0')} total items)
                  </Typography>
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
        
        {/* Mobile Filter Drawer */}
        <MobileFilterDrawer />
      </Box>
    </Box>
  );
};

export default MarketPlaceTab;